import { FC, Fragment } from 'react';
import { Box, Divider, Skeleton, Svg, Text } from '@hopskipdrive/react-components';
import { colorPalette } from '@rideiq/shared/components/variables';

import { actions, Can, subjects } from '../../../contexts/AbilityContext';
import { useFlags } from '../../../contexts/LDContext';
import { RiderCard } from '../../components/RiderCard';

import { RiderLocation } from './Location/Location.types';
import { AddLocation } from './AddLocation';
import { Location } from './Location';
import { Label, LocationContainer } from './styles';
import { useLocationsForRider } from './useLocationsForRider';

interface RiderLocationsProps {
  riderSlug: string;
  isRiderActive: boolean;
}
export interface IRiderLocations {
  isLoading: boolean;
  isRiderActive: boolean;
  associatedLocations: RiderLocation[];
  riderSlug: string;
  addLocation: (data: RiderLocationFormData, locationDetails: RiderLocation) => Promise<boolean>;
  editLocation: (data: RiderLocationFormData, locationDetails: RiderLocation) => Promise<boolean>;
  removeLocation: (locationId: number) => Promise<boolean>;
}

export const RiderLocations: FC<RiderLocationsProps> = ({ riderSlug, isRiderActive }) => {
  const { isLoading, associatedLocations, addLocation, editLocation, removeLocation } = useLocationsForRider(riderSlug);

  return (
    <Presentation
      isLoading={isLoading}
      isRiderActive={isRiderActive}
      associatedLocations={associatedLocations}
      riderSlug={riderSlug}
      addLocation={addLocation}
      editLocation={editLocation}
      removeLocation={removeLocation}
    />
  );
};

export const Presentation: FC<IRiderLocations> = ({
  isLoading,
  isRiderActive,
  associatedLocations,
  riderSlug,
  addLocation,
  editLocation,
  removeLocation,
}) => {
  const { iepStructuredNotesEnabled } = useFlags();
  const useNewForm = iepStructuredNotesEnabled;

  return (
    <RiderCard
      gradientOverlay={true}
      height="227px"
      heading={
        <Box display="flex" justifyContent="space-between" alignContent="center">
          <Label>Locations</Label>
          {!useNewForm && (
            <Can I={actions.edit} a={subjects.rider}>
              <AddLocation
                addLocation={addLocation}
                associatedLocations={associatedLocations}
                isRiderActive={isLoading ? false : isRiderActive}
                riderSlug={riderSlug}
              />
            </Can>
          )}
        </Box>
      }
      data-testid="rider-locations-card"
    >
      {isLoading && <LocationsSkeleton />}
      {associatedLocations.map((location, index) => (
        <Fragment key={index}>
          <Location
            riderSlug={riderSlug}
            location={location}
            associatedLocations={associatedLocations}
            removeLocation={removeLocation}
            editLocation={editLocation}
            isRiderActive={isRiderActive}
          />
          <Divider my={3} data-testid="rider-location-divider" aria-hidden="true" />
        </Fragment>
      ))}
      {associatedLocations.length === 0 && isLoading === false && (
        <>
          <Box display="flex" alignItems="baseline" gap="8px">
            <Svg height="13px" width="25px" icon="residence" color={colorPalette.red40} />
            <Box>
              <Text fontWeight={300} textColor="red40">
                <i>No Locations saved.</i>
              </Text>
              <Text fontWeight={300} textColor="secondaryText">
                Add locations to a Rider’s profile to quickly access their most commonly used destinations everywhere in
                RideIQ.
              </Text>
            </Box>
          </Box>
          <Divider my={3} aria-hidden="true" />
        </>
      )}
    </RiderCard>
  );
};

const LocationsSkeleton = () => (
  <LocationContainer>
    <Box mr={2}>
      <Skeleton variant="circular" width={20} height={20} />
    </Box>

    <Box display="flex" flexDirection="column">
      <Skeleton width={150} />
      <Skeleton width={50} />
      <Skeleton width={100} />
      <Skeleton width={100} />
    </Box>
  </LocationContainer>
);
